import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Sparkles as SparklesIcon, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

export default function APropos() {
    return (
        <div data-testid="apropos-page">
            <Seo
                title="À propos · Foundation of Light"
                description="Foundation of Light, un pont vivant entre le visible et l'invisible : manifeste, mission et accompagnement de l'être en transition par les soins de lumière."
                path="/a-propos"
            />
            <section className="relative overflow-hidden bg-background" data-testid="foundation-manifesto">
                <div className="absolute inset-0 pointer-events-none opacity-[0.06]">
                    <div className="absolute top-40 -left-20 w-96 h-96 rounded-full bg-secondary blur-3xl" />
                    <div className="absolute bottom-40 -right-20 w-[28rem] h-[28rem] rounded-full bg-primary blur-3xl" />
                </div>

                <div className="relative max-w-4xl mx-auto px-6 py-32">
                    <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="text-center mb-16">
                        <div className="text-xs tracking-[0.45em] uppercase text-primary mb-6 font-medium">Foundation of Light</div>
                        <h1 className="font-serif text-4xl sm:text-5xl leading-tight mb-8">
                            Un lieu pour celles et ceux qui ressentent<br />
                            <span className="italic">que l'humanité s'éveille à quelque chose de plus grand.</span>
                        </h1>
                    </motion.div>

                    <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="space-y-6 text-lg text-foreground/80 leading-relaxed">
                        <p>
                            Il arrive, dans l'évolution de chaque être, un moment où les questions deviennent plus profondes,
                            où la perception s'affine, et où le monde ne peut plus être vécu tout à fait comme auparavant.
                            <em className="text-foreground/90"> Quelque chose en nous se souvient.</em>
                        </p>
                        <p>
                            D'une connexion à plus grand que soi. D'une intelligence plus vaste. D'une conscience qui s'étend
                            bien au-delà de ce que nos sens physiques peuvent percevoir.
                        </p>
                        <p>
                            La Foundation of Light est un lieu sacré de rassemblement pour celles et ceux qui ressentent
                            ce mouvement intérieur et qui sont appelés à explorer l'évolution de la conscience humaine,
                            les dimensions subtiles de l'existence et la profonde transformation que traverse notre planète.
                        </p>
                        <p className="font-serif italic text-2xl sm:text-3xl text-foreground/90 py-4">
                            Un espace d'éveil, de transmission, d'harmonisation et de connexion.
                        </p>
                        <p>
                            Un espace où différents chemins peuvent se rencontrer. Où la curiosité peut coexister avec la
                            connaissance intérieure. Où chacun est invité à écouter ce qui résonne profondément en lui.
                        </p>
                    </motion.div>

                    {/* Un pont entre les mondes */}
                    <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="mt-24">
                        <div className="text-sm tracking-[0.3em] uppercase text-secondary mb-4 font-medium">I · Un pont entre les mondes</div>
                        <h2 className="font-serif text-3xl sm:text-4xl mb-6">Née d'un appel à relier le visible et l'invisible.</h2>
                        <div className="space-y-6 text-base sm:text-lg text-foreground/80 leading-relaxed">
                            <p>
                                La Foundation of Light est née d'un appel reçu par Olivier en <strong className="not-italic font-semibold">décembre 2024</strong> —
                                un rêve dans lequel lui fut transmis le nom, la mission et le logo de cette organisation :
                                <em> créer un espace pour recevoir et transmettre la Lumière</em>, un pont vivant entre le visible
                                et l'invisible, entre la conscience humaine et les plans supérieurs qui accompagnent son évolution.
                            </p>
                            <p>
                                Ce qui n'était d'abord qu'une vision nocturne s'est peu à peu incarné :
                                le nom <em>Foundation of Light</em>, le symbole gravé dans le rêve, et la certitude profonde
                                qu'un lieu — sacré, ouvert et vivant — devait naître pour accueillir cette transmission.
                            </p>
                            <p>
                                À travers des ateliers, des transmissions, des rencontres et différents événements, nous partageons
                                ce qui est reçu à travers la perception subtile et la connexion aux plans supérieurs de conscience.
                                Ces transmissions peuvent ouvrir sur de nombreuses dimensions de l'existence — les consciences
                                angéliques, galactiques et intraterrestres, mais également sur l'évolution de la conscience de
                                Gaïa et l'émergence de ce qui est appelé la <em>Nouvelle Terre</em>.
                            </p>
                            <p>
                                L'intention n'est pas d'imposer une croyance, ni de proposer des vérités à suivre. Il s'agit
                                d'ouvrir des espaces de résonance. D'offrir des perspectives, des fréquences et des expériences
                                susceptibles d'accompagner chacun dans sa reconnexion à sa propre connaissance intérieure.
                            </p>
                        </div>
                    </motion.div>

                    {/* Harmoniser l'être en transition */}
                    <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="mt-24">
                        <div className="text-sm tracking-[0.3em] uppercase text-secondary mb-4 font-medium">II · Harmoniser l'être en transition</div>
                        <h2 className="font-serif text-3xl sm:text-4xl mb-6">L'humanité traverse une transformation profonde.</h2>
                        <div className="space-y-6 text-base sm:text-lg text-foreground/80 leading-relaxed">
                            <p>
                                À mesure que la conscience s'expanse, de nombreux êtres vivent des changements qu'il peut devenir
                                difficile de comprendre à travers les cadres habituels. D'anciens schémas peuvent refaire surface.
                                Les sensibilités peuvent s'intensifier. Les relations, les perceptions et les façons d'habiter le
                                monde peuvent être profondément questionnées.
                            </p>
                            <p>
                                Cette transition peut être vécue sur de nombreux plans — physique, émotionnel, énergétique, mental et spirituel.
                            </p>
                            <p>
                                La Foundation of Light propose également des accompagnements individuels dédiés à l'harmonisation
                                et à la cohérence de l'être, pour celles et ceux qui ressentent l'appel d'avancer dans cette
                                transition avec davantage de conscience et d'alignement. Ces séances sont des espaces de reconnexion,
                                permettant à chacun d'explorer son propre chemin énergétique et multidimensionnel.
                            </p>
                            <blockquote className="border-l-2 border-secondary pl-6 py-2 font-serif italic text-xl text-foreground/90">
                                L'intention n'est pas de changer qui vous êtes. Elle est de vous accompagner à vous souvenir
                                plus pleinement de qui vous êtes en train de devenir.
                            </blockquote>
                        </div>
                    </motion.div>

                    {/* Une mission collective */}
                    <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="mt-24">
                        <div className="text-sm tracking-[0.3em] uppercase text-secondary mb-4 font-medium">III · Une mission collective</div>
                        <h2 className="font-serif text-3xl sm:text-4xl mb-6">Un espace vivant, tissé par de nombreux êtres.</h2>
                        <div className="space-y-6 text-base sm:text-lg text-foreground/80 leading-relaxed">
                            <p>
                                Je porte cette mission en tant que pont interdimensionnel et guérisseur intergalactique,
                                en connexion avec un collectif d'êtres de lumière avec lequel cette mission se révèle et se transmet.
                                Mais cette mission ne m'appartient pas.
                            </p>
                            <p>
                                La Foundation of Light a vocation à devenir un espace vivant de connexion entre de nombreux êtres
                                qui, chacun à leur manière, participent à l'éveil de la conscience humaine et à l'émergence d'une
                                nouvelle relation à la Vie, à la Terre et aux dimensions plus vastes de l'existence.
                            </p>
                            <p>
                                Certains viendront à travers les enseignements. D'autres à travers les soins et l'harmonisation.
                                D'autres encore par la méditation, la transmission, la perception intuitive, la création ou
                                simplement par leur présence.
                            </p>
                            <p className="font-serif italic text-2xl text-foreground/90">
                                Il n'existe pas un chemin unique. Il existe seulement l'invitation à suivre ce qui résonne
                                profondément en vous.
                            </p>
                        </div>
                    </motion.div>

                    {/* Closing invitation */}
                    <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} variants={fadeUp} className="mt-24 rounded-3xl border border-secondary/40 bg-gradient-to-br from-secondary/10 via-transparent to-primary/5 p-10 sm:p-14 text-center">
                        <SparklesIcon className="w-8 h-8 text-secondary mx-auto mb-6" />
                        <p className="text-base sm:text-lg text-foreground/80 leading-relaxed max-w-2xl mx-auto mb-6">
                            Peut-être ressentez-vous déjà l'appel. Peut-être avez-vous toujours senti que l'existence
                            était plus vaste que ce qui pouvait être vu. Peut-être vous sentez-vous connecté aux étoiles,
                            à d'autres dimensions, aux consciences angéliques ou galactiques, aux mondes intraterrestres,
                            ou simplement à cette sensation profonde que l'humanité entre aujourd'hui dans une nouvelle
                            étape de son évolution.
                        </p>
                        <p className="text-base sm:text-lg text-foreground/80 leading-relaxed max-w-2xl mx-auto mb-8">
                            Peut-être n'avez-vous pas encore les mots pour décrire ce que vous ressentez.
                            <strong className="not-italic"> Vous n'en avez pas besoin.</strong>
                        </p>
                        <p className="font-serif text-2xl sm:text-3xl italic text-foreground/90 mb-2">
                            Bienvenue à la Foundation of Light.
                        </p>
                        <p className="text-sm tracking-[0.3em] uppercase text-primary/80 mb-10">
                            Un espace de conscience · Un pont entre les dimensions · Un rassemblement d'êtres qui s'éveillent à la Lumière
                        </p>
                        <div className="flex flex-wrap gap-3 justify-center">
                            <Link to="/histoire">
                                <Button variant="outline" className="rounded-full h-12 px-6" data-testid="apropos-cta-story">
                                    Mon histoire <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                            </Link>
                            <Link to="/logo">
                                <Button variant="outline" className="rounded-full h-12 px-6" data-testid="apropos-cta-logo">
                                    Notre logo <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}
